import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BatchExportProgress } from 'src/app/state-management';

@Component({
    selector: 'app-batch-export',
    templateUrl: './batch-export.component.html',
    styleUrls: ['./batch-export.component.css'],
    standalone: false
})
export class BatchExportComponent implements OnInit, OnDestroy {
  @Input() batchExportProblems: string[] = [];
  @Input() includeHistory: boolean = false;
  
  problemsToLoad: string[] = [];
  historiesToLoad: string[] = [];

  currentProblemLoaded: Boolean = false;
  currentProblemHistoryLoaded: Boolean = false;

  jsPDFDoc!: jsPDF;
  pdfHeight: number;
  pdfWidth: number;

  readonly PDF_PageTop_Gap = 10;

  constructor(private activeModal: NgbActiveModal) { }

  ngOnInit(): void {
    this.jsPDFDoc = new jsPDF('p', 'mm', 'a4');
    this.pdfWidth = this.jsPDFDoc.internal.pageSize.getWidth();
    this.pdfHeight = this.jsPDFDoc.internal.pageSize.getHeight();
    // const imgWidth = 210;
    // const pageHeight = 297;
    // const imgWidth = 208;
    // const pageHeight = 295;


    this.problemsToLoad.push(this.batchExportProblems[0]);
    this.updateExportPanel();

    document.addEventListener('batchExport_problemLoaded', () => {
      this.currentProblemLoaded = true;
      this.updateExportPanel();

      if (this.includeHistory) this.historiesToLoad.push(this.problemsToLoad[this.problemsToLoad.length - 1]);
      else {
        let className = `.${this.getContainerClassName(this.problemsToLoad[this.problemsToLoad.length - 1])}`;
        this.setRemainingSpaceForPageGap(className);
        this.queueNextProblem();
      }

      this.scrollToBottom();
    });

    document.addEventListener('batchExport_historyLoaded', () => {
      this.currentProblemHistoryLoaded = true;
      this.updateExportPanel();

      let className = `.${this.getContainerClassName(this.problemsToLoad[this.problemsToLoad.length - 1])}`;
      this.setRemainingSpaceForPageGap(className);
      this.queueNextProblem();
      this.scrollToBottom();
    });
  }

  @HostListener('pagehide')
  ngOnDestroy() {
    this.problemsToLoad = [];
    this.historiesToLoad = [];
    this.currentProblemLoaded = false;
    this.currentProblemHistoryLoaded = false;

    document.removeAllListeners('batchExport_problemLoaded');
    document.removeAllListeners('batchExport_historyLoaded');
    document.removeAllListeners('mapLayerLoaded');
  }

  scrollToBottom = () => {
    const container = document.getElementById("batchExport-container");
    container.scrollTop = container.scrollHeight;
  }

  getClassName = (problemNo: string, isDetails: boolean) => `${problemNo}_${isDetails ? "details" : "history"}`;
  getContainerClassName = (problemNo: string) => `${problemNo}_batchContainer`;

  updateExportPanel(isCompleteOverride: boolean = false) {
    document.dispatchEvent(new CustomEvent("exportPanelUpdate", {
      detail: {
        currentProblem: this.problemsToLoad[this.problemsToLoad.length - 1],
        status: this.getProgressStatus(isCompleteOverride),
        problemLoading: this.problemsToLoad.length
      }
    }));
  }

  getProgressStatus(isComplete: boolean) {
    if (isComplete) {
      return BatchExportProgress.SummaryComplete;
    }

    let isAllProblemsProcessed = this.problemsToLoad.length === this.batchExportProblems.length;
    if (isAllProblemsProcessed && this.currentProblemLoaded && (this.includeHistory ? this.currentProblemHistoryLoaded : true)) {
      return BatchExportProgress.Summarizing;
    }

    if (this.includeHistory && this.currentProblemLoaded) {
      return BatchExportProgress.LoadingHistory;
    }

    return BatchExportProgress.LoadingDetails;
  }

  queueNextProblem() {
    if (this.currentProblemLoaded && (this.includeHistory ? this.currentProblemHistoryLoaded : true)) {      
      this.currentProblemLoaded = false;
      this.currentProblemHistoryLoaded = false;
      
      let lastProblem = this.problemsToLoad[this.problemsToLoad.length - 1];
      let indexProblem = this.batchExportProblems.findIndex(problem => problem === lastProblem);
      
      if (indexProblem === this.batchExportProblems.length - 1) {
        this.saveAsPDF();
        return;
      }
      
      this.problemsToLoad.push(this.batchExportProblems[indexProblem + 1]);
      this.updateExportPanel();
    }
  }

  setRemainingSpaceForPageGap(className: string) {
    // Only do the page resizing for the problems if they aren't the last one being exported. Last doesn't need padding.
    if (this.problemsToLoad.length !== this.batchExportProblems.length) {
      let problemContainer = (document.querySelector(className) as HTMLDivElement);
      let problemHeight = problemContainer.clientHeight;
      let problemWidth = problemContainer.clientWidth;     
      
      let numPagesToRender = this.getNumPagesUsedNeededForExport(problemHeight, problemWidth);
      let pageHeightFullPage = (numPagesToRender * this.pdfHeight * problemWidth) / (this.pdfWidth);
      problemContainer.style.height = `${pageHeightFullPage}px`;
    }
  }

  getNumPagesUsedNeededForExport(height: number, width: number) {
    let numPagesUsed = (height * this.pdfWidth) / (width * this.pdfHeight);
    let intNumPagesUsed = parseInt(`${numPagesUsed}`);
    
    let numPagesToRender = intNumPagesUsed;
    if (intNumPagesUsed < numPagesUsed) {
      numPagesToRender++;
    }

    return numPagesToRender;
  }

  saveAsPDF() {
    // Need to specify the boundary of SVG in order for html2canvas to capture map markers (fontawesome).
    const svgElements = document.body.querySelectorAll('svg.svg-inline--fa');
    svgElements.forEach((item) => {
      item.setAttribute('width', item.getBoundingClientRect().width.toString());
      item.setAttribute('height', item.getBoundingClientRect().height.toString());
    });

    html2canvas(document.getElementById('batchExport-container'), { useCORS: true, allowTaint: false, removeContainer: false }).then(async canvas => {
      this.updateExportPanel(true);      
      const imgData = canvas.toDataURL('image/jpeg', 0.5);
      const imgHeight = canvas.height * this.pdfWidth / canvas.width;
      const format = 'JPEG';

      // put initial gap of 10 to give space at the top of the initial page
      let positionY = this.PDF_PageTop_Gap;
      let positionX = 0;
      let heightLeft = imgHeight;
  
      this.jsPDFDoc.addImage(imgData, format, positionX, positionY, this.pdfWidth, imgHeight);
      heightLeft -= this.pdfHeight;
      
      while (heightLeft >= 0) {
        let currentNumPages = this.jsPDFDoc.getNumberOfPages();
        this.jsPDFDoc.addPage();

        // wait for the page to be added before pasting content to prevent text loading on top of one another
        await new Promise(resolve => {
          let interval = setInterval(() => {
            if (currentNumPages === this.jsPDFDoc.getNumberOfPages() - 1) {
              // add padding to other pages as well and recalculate image position
              positionY = this.PDF_PageTop_Gap + heightLeft - imgHeight;
              this.jsPDFDoc.addImage(imgData, format, positionX, positionY, this.pdfWidth, imgHeight);
              heightLeft -= this.pdfHeight;
  
              clearInterval(interval);
              resolve(true);
            }
          }, 25);
        });
      }

      setTimeout(() => {
        let blobPDF = new Blob([this.jsPDFDoc.output('blob')], { type: 'application/pdf' });
        let fileUrl = URL.createObjectURL(blobPDF);
        let link = document.createElement('a');
        
        window.open(fileUrl);
        link.download = 'Export.pdf';
        link.href = fileUrl;
        link.click();
    
        URL.revokeObjectURL(fileUrl);
        link.remove();
        link = null;
        blobPDF = null;
        this.jsPDFDoc = null;
    
        this.handleCanvasCleanup();
        this.activeModal.close();
      }, 1500);
    });
  }

  handleCanvasCleanup() {
    document.dispatchEvent(new Event('destroyMap'));
    document.dispatchEvent(new Event('destroyDocumentClone'));
    document.querySelectorAll('.html2canvas-container').forEach((el: any) => {
      let iframe = el.contentWindow;
      if (el) {
        var removeChilds = (node: HTMLElement) => {
          var last;
          while (last = node.lastChild) node.removeChild(last);
        };

        removeChilds(iframe.document);
        el.src = 'about:blank';
        iframe.document.write('');
        iframe.document.clear();
        iframe.close();
        el.remove();

        iframe = null;
        el = null;
      }
    });
  }
}
